import React, { useEffect, useRef, useState } from "react";
import { TelnyxRTC } from "@telnyx/webrtc";
import api from "@/lib/api";
import { routeDestination } from "@/lib/sipTrunk";
import { toast } from "sonner";
import { Phone, PhoneSlash, Backspace, ArrowsClockwise, WifiHigh, WifiSlash, Gear, CaretDown } from "@phosphor-icons/react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export function Dialpad({ token, did, sipUser, sipPass, onRefreshToken }) {
  const clientRef = useRef(null);
  const callRef = useRef(null);
  const startRef = useRef(null);
  const [status, setStatus] = useState("connecting");
  const [num, setNum] = useState("");
  const [incoming, setIncoming] = useState(null);
  const [creds, setCreds] = useState({ user: sipUser || "", pass: sipPass || "" });
  const [useSip, setUseSip] = useState(false);
  const [showSip, setShowSip] = useState(false);

  const finalize = () => {
    const c = callRef.current;
    const rawDest = c?.options?.destinationNumber || num;
    const dest = String(rawDest).startsWith("sip:") ? num : rawDest;
    if (startRef.current && dest) {
      const secs = Math.round((Date.now() - startRef.current) / 1000);
      if (secs > 0) api.post("/telecom/usage", { destination: dest, seconds: secs }).catch(() => {});
    }
    startRef.current = null; callRef.current = null; setIncoming(null); setStatus("ready");
  };

  useEffect(() => {
    const opts = (useSip && creds.user && creds.pass)
      ? { login: creds.user, password: creds.pass }
      : (token ? { login_token: token } : null);
    if (!opts) { setStatus("error"); return; }
    setStatus("connecting");
    let client;
    try {
      client = new TelnyxRTC(opts);
      client.remoteElement = "telnyx-remote-audio";
      client.on("telnyx.ready", () => setStatus("ready"));
      client.on("telnyx.error", () => setStatus("error"));
      client.on("telnyx.socket.error", () => setStatus("error"));
      client.on("telnyx.notification", (n) => {
        if (n.type !== "callUpdate" || !n.call) return;
        const c = n.call; callRef.current = c;
        const st = c.state;
        if (st === "ringing") { setIncoming(c); setStatus("ringing"); }
        else if (st === "active") { setStatus("oncall"); if (!startRef.current) startRef.current = Date.now(); }
        else if (["hangup", "destroy", "done"].includes(st)) { finalize(); }
      });
      client.connect();
      clientRef.current = client;
    } catch { setStatus("error"); }
    return () => { try { client && client.disconnect(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, useSip, creds.user, creds.pass]);

  const dial = async () => {
    if (status !== "ready") { toast.error("Softphone not ready yet"); return; }
    if (!num) return;
    try {
      startRef.current = null;
      const c = clientRef.current.newCall({ destinationNumber: await routeDestination(num), callerNumber: did, audio: true, video: false });
      callRef.current = c; setStatus("oncall");
    } catch { toast.error("Could not place call"); }
  };
  const answer = () => { try { incoming?.answer(); startRef.current = Date.now(); setStatus("oncall"); setIncoming(null); } catch {} };
  const hangup = () => { try { callRef.current?.hangup?.(); } catch {} finalize(); };
  const applySip = () => {
    if (!creds.user || !creds.pass) { toast.error("Enter SIP username and password"); return; }
    setUseSip(true); setShowSip(false); toast.success("Reconnecting with SIP credentials…");
  };

  const dot = status === "ready" || status === "oncall" ? "bg-emerald-500" : status === "error" ? "bg-red-500" : "bg-amber-500";
  const label = { connecting: "Connecting…", ready: "Ready", oncall: "On call", ringing: "Incoming call", error: "Offline" }[status];

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="softphone-dialpad">
      <audio id="telnyx-remote-audio" autoPlay />
      <div className="mb-4 flex items-center gap-2">
        {status === "error" ? <WifiSlash size={18} className="text-red-500" /> : <WifiHigh size={18} className="text-emerald-500" />}
        <p className="font-semibold">In-App Softphone</p>
        <span className={`ml-1 h-2 w-2 rounded-full ${dot}`} />
        <span className="text-xs text-muted-foreground" data-testid="softphone-status">{label}{useSip ? " · SIP" : ""}</span>
        <button onClick={() => setShowSip((s) => !s)} title="Program SIP credentials" className="ml-auto rounded-lg p-1.5 text-muted-foreground hover:text-primary" data-testid="softphone-sip-toggle"><Gear size={16} /></button>
        <button onClick={onRefreshToken} title="Reconnect" className="rounded-lg p-1.5 text-muted-foreground hover:text-primary" data-testid="softphone-refresh"><ArrowsClockwise size={16} /></button>
      </div>

      {showSip && (
        <div className="mb-4 space-y-2 rounded-xl border border-border bg-secondary/50 p-3" data-testid="sip-config">
          <p className="text-xs font-semibold text-muted-foreground">Program dialer with SIP credentials</p>
          <input value={creds.user} onChange={(e) => setCreds((c) => ({ ...c, user: e.target.value }))} placeholder="SIP username" data-testid="sip-user" className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono-metric text-sm" />
          <input value={creds.pass} onChange={(e) => setCreds((c) => ({ ...c, pass: e.target.value }))} placeholder="SIP password" type="password" data-testid="sip-pass" className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono-metric text-sm" />
          <div className="flex items-center gap-2">
            <button onClick={applySip} data-testid="sip-apply" className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground">Connect with SIP</button>
            {useSip && <button onClick={() => { setUseSip(false); setShowSip(false); }} className="text-xs text-muted-foreground hover:text-primary">Use secure token instead</button>}
          </div>
          <p className="text-[11px] text-muted-foreground">Same credentials also work in any external SIP dialer — contact iYeneh support for the server address.</p>
        </div>
      )}

      {incoming && (
        <div className="mb-4 flex items-center justify-between rounded-xl bg-emerald-500/10 px-4 py-3" data-testid="incoming-call">
          <span className="text-sm font-semibold text-emerald-500">Incoming call…</span>
          <button onClick={answer} data-testid="answer-call" className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-white">Answer</button>
        </div>
      )}

      <input
        data-testid="dialpad-input" value={num} onChange={(e) => setNum(e.target.value.replace(/[^0-9+*#]/g, ""))}
        placeholder="+1 (202) 555-0100" inputMode="tel"
        className="w-full rounded-xl border border-input bg-background px-4 py-3 text-center font-mono-metric text-lg outline-none focus:border-primary" />

      <div className="mt-4 grid grid-cols-3 gap-2">
        {KEYS.map((k) => (
          <button key={k} data-testid={`dialpad-key-${k}`} onClick={() => setNum((n) => n + k)}
            className="rounded-xl border border-border bg-background py-3 font-mono-metric text-lg font-semibold transition-colors hover:bg-secondary">{k}</button>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        {status === "oncall" ? (
          <button onClick={hangup} data-testid="dialpad-hangup" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500 py-3.5 font-bold text-white">
            <PhoneSlash size={20} weight="fill" /> End call
          </button>
        ) : (
          <button onClick={dial} disabled={!num || status !== "ready"} data-testid="dialpad-call" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 font-bold text-white disabled:opacity-50">
            <Phone size={20} weight="fill" /> Call
          </button>
        )}
        <button onClick={() => setNum((n) => n.slice(0, -1))} data-testid="dialpad-back" className="rounded-full border border-border p-3.5 text-muted-foreground hover:text-primary"><Backspace size={20} /></button>
      </div>
      <p className="mt-3 text-center text-[11px] text-muted-foreground">Dial any global landline or mobile in E.164 format. Your desk phone rings at the same time.</p>
    </div>
  );
}
